import { axiosPrivate } from "../api/axios";
import { useEffect, useRef } from "react";
import useRefreshToken from "./useRefreshToken";
import useAuth from "./useAuth";

const useAxiosPrivate = () => {
  const refresh = useRefreshToken();
  const { logout } = useAuth();
  const refreshRef = useRef(refresh);
  const logoutRef = useRef(logout);

  // Keep references fresh without triggering interceptor recreation
  useEffect(() => {
    refreshRef.current = refresh;
    logoutRef.current = logout;
  }, [refresh, logout]);

  useEffect(() => {
    const requestIntercept = axiosPrivate.interceptors.request.use(
      async (config) => {
        let token = localStorage.getItem("accessToken");
        let expiry = localStorage.getItem("tokenExpiry");

        // ✅ If token expired → refresh BEFORE request
        if (expiry && Date.now() > Number(expiry)) {
          try {
            const newToken = await refreshRef.current();
            const newExpiry = Date.now() + 15 * 60 * 1000;

            localStorage.setItem("accessToken", newToken);
            localStorage.setItem("tokenExpiry", newExpiry);

            token = newToken;
          } catch (err) {
            logoutRef.current();
            return Promise.reject(err);
          }
        }

        if (token) {
          config.headers["Authorization"] = `Bearer ${token}`;
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseIntercept = axiosPrivate.interceptors.response.use(
      (response) => response,
      async (error) => {
        const prevRequest = error?.config;

        if (error?.response?.status === 401 && !prevRequest?.sent) {
          prevRequest.sent = true;

          try {
            const newToken = await refreshRef.current();
            const newExpiry = Date.now() + 15 * 60 * 1000;

            localStorage.setItem("accessToken", newToken);
            localStorage.setItem("tokenExpiry", newExpiry);

            prevRequest.headers["Authorization"] = `Bearer ${newToken}`;
            return axiosPrivate(prevRequest);
          } catch (err) {
            logoutRef.current();
            return Promise.reject(err);
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axiosPrivate.interceptors.request.eject(requestIntercept);
      axiosPrivate.interceptors.response.eject(responseIntercept);
    };
  }, []);

  return axiosPrivate;
};

export default useAxiosPrivate;
